import React, { useMemo, useCallback, KeyboardEvent } from 'react';
import classNames from 'classnames';
import { canShowShopRatings, generateImageSrc, isShopOnlyTakeAway } from '@box/utils';
import { translate } from '@box-core/services';
import type { PromoVisualOptions } from '@box-types';
import { ShopTileDeliveryInfo } from '../shop-tile-delivery-info';
import { ImageTile, PromoBadge } from '@box/component-library';
import { ShopRating } from '../shop-rating';
import { PromoTagComponent } from '../promo-tag';
import { ShopTileProps } from '../shop-tile/shop-tile.types';

const ShopTileCompact: React.FC<ShopTileProps> = ({
  shop,
  layout = 'HORIZONTAL',
  disableAnimation = false,
  onRouterClick
}) => {
  const {
    name,
    logo,
    isNew,
    disabledText,
    ratingCounter,
    promo,
    locationKey,
    vanity_url,
    cuisinesText,
    mainCuisineText,
    promotedTag
  } = shop;

  const logoUrl = useMemo(() => generateImageSrc(logo), [logo]);
  const hasTakeAwayLabel = useMemo(() => isShopOnlyTakeAway(shop), [shop]);
  const routerLink = `/delivery/${locationKey}/${vanity_url}`;
  const showRating = useMemo(() => canShowShopRatings(ratingCounter), [ratingCounter]);

  const firstGroupTagOptions: PromoVisualOptions = promo?.firstGroupShortOptions;
  const secondGroupTagOptions: PromoVisualOptions = promo?.secondGroupShortOptions;
  const firstGroupBadgeOptions: PromoVisualOptions = promo?.firstGroupLongOptions;
  const secondGroupBadgeOptions: PromoVisualOptions = promo?.secondGroupLongOptions;

  const isVertical = layout === 'VERTICAL';
  const isHorizontal = layout === 'HORIZONTAL';

  const cuisineText = layout === 'HORIZONTAL' ? cuisinesText : mainCuisineText;

  const showTags = isVertical && (firstGroupTagOptions || secondGroupTagOptions);
  const showBadges = isHorizontal && (firstGroupBadgeOptions || secondGroupBadgeOptions);
  const showPromotedTag = isHorizontal && Boolean(promotedTag);
  const translateReference = translate;

  const onShopClick = useCallback(() => {
    onRouterClick(routerLink);
  }, [onRouterClick]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onRouterClick(routerLink);
      }
    },
    [onRouterClick]
  );

  return (
    <div
      onClick={() => onShopClick()}
      onKeyDown={(e) => handleKeyDown(e)}
      role="link"
      tabIndex={0}
      className={classNames('shop-tile-compact-component', {
        'shop-tile-compact-component-horizontal': isHorizontal,
        'shop-tile-compact-component-vertical': isVertical
      })}
    >
      <div className="shop-tile-compact-component-image">
        {showTags && (
          <div className="shop-tile-compact-component-image-tags">
            {firstGroupTagOptions && (
              <div className="box-promo-tag">
                <PromoTagComponent options={firstGroupTagOptions} enableAnimations={!disableAnimation} />
              </div>
            )}
            {secondGroupTagOptions && (
              <div className="box-promo-tag">
                <PromoTagComponent options={secondGroupTagOptions} enableAnimations={!disableAnimation} />
              </div>
            )}
          </div>
        )}
        <div className="image-tile hoverable-animated">
          <ImageTile
            imgUrl={logoUrl}
            altText={`${name} logo`}
            overlayText={disabledText}
            bottomOverlayText={hasTakeAwayLabel ? 'Take Away' : null}
            translate={translateReference}
          />
        </div>
      </div>

      <div
        className={classNames('shop-tile-compact-component-details', {
          'shop-tile-compact-component-details-disabled': disabledText
        })}
      >
        {showBadges && (
          <div className="shop-tile-compact-component-details-badges">
            {firstGroupBadgeOptions && <PromoBadge {...firstGroupTagOptions} />}
            {secondGroupBadgeOptions && <PromoBadge {...secondGroupBadgeOptions} />}
          </div>
        )}

        <div className="shop-tile-compact-component-info">
          <div
            className={classNames('shop-tile-compact-component-info-name shop-tile-compact-component-info-row', {
              'shop-tile-compact-component-info-name-with-promoted-tag': showPromotedTag && !showBadges
            })}
          >
            {name}
          </div>

          <div className="shop-tile-compact-component-info-row">
            {isNew && (
              <div className="shop-tile-compact-component-info-row-block shop-tile-compact-component-info-new-tag">
                NEW
              </div>
            )}
            {showRating && (
              <div className="shop-tile-compact-component-info-row-block">
                <ShopRating shop={shop} />
              </div>
            )}
            <span className="shop-tile-compact-component-info-row-block shop-tile-compact-component-info-cuisine-text">
              {cuisineText}
            </span>
          </div>

          <div className="shop-tile-compact-component-info-row">
            <ShopTileDeliveryInfo shop={shop} />
          </div>
        </div>

        {showPromotedTag && (
          <span className="shop-tile-compact-component-details-promoted-tag ods-typography-microcopy-bold">
            {promotedTag}
          </span>
        )}
      </div>
    </div>
  );
};

export default React.memo(ShopTileCompact, (prev, next) => {
  return prev.shop._id === next.shop._id;
});
